import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Icon from '../../ui/Icon';
import Avatar from '../../ui/Avatar';
import { T } from '../../constants/mockData';
import { groupsAPI, usersAPI, ticketsAPI } from '../../services/api';

// Only show workspace Action Taker groups in ticket assignment
const WORKSPACE_GROUP_NAMES = ['Action Taker 1', 'Action Taker 2', 'Action Taker 3'];

const DROPDOWN_W = 360;
const DROPDOWN_H = 380;

export default function InlineAssignedToDropdown({ ticketId, value, onAssigned, groupName }) {
  const [open, setOpen]               = useState(false);
  const [search, setSearch]           = useState('');
  const [tab, setTab]                 = useState('groups');
  const [groups, setGroups]           = useState([]);
  const [agents, setAgents]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [saving, setSaving]           = useState(false);
  const [portalStyle, setPortalStyle] = useState({});
  const btnRef  = useRef(null);
  const dropRef = useRef(null);

  // Load groups + agents from API (always fresh from database)
  const loadAssignData = useCallback(() => {
    setLoading(true);
    return Promise.all([groupsAPI.list(), usersAPI.list()])
      .then(([gData, uData]) => {
        const allGroups = Array.isArray(gData) ? gData : (gData.groups || []);
        const gList = allGroups.filter(g => WORKSPACE_GROUP_NAMES.includes(g.name));
        const uList = Array.isArray(uData) ? uData : (uData.users || []);
        const aList = uList.filter(u => u.role === 'agent' && u.is_active !== false);
        setGroups(gList);
        setAgents(aList);
        setActiveGroup(prev => {
          if (prev && gList.some(g => g.id === prev.id)) return prev;
          return gList[0] || null;
        });
      })
      .catch(err => console.error('Failed to load assign data', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAssignData(); }, [loadAssignData]);

  // Close on outside click (checks both button and portal panel)
  useEffect(() => {
    if (!open) return;
    const handler = e => {
      if (btnRef.current?.contains(e.target)) return;
      if (dropRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Calculate portal position every time it opens
  const handleOpen = useCallback(() => {
    if (!btnRef.current) { setOpen(o => !o); return; }

    if (open) { setOpen(false); return; }

    const rect    = btnRef.current.getBoundingClientRect();
    const PAD     = 8;
    const scrollX = window.scrollX || 0;
    const scrollY = window.scrollY || 0;

    // Horizontal
    const spaceRight = window.innerWidth - rect.left;
    const left = spaceRight >= DROPDOWN_W + PAD
      ? rect.left + scrollX
      : rect.right + scrollX - DROPDOWN_W;

    // Vertical
    const spaceBelow = window.innerHeight - rect.bottom - PAD;
    const spaceAbove = rect.top - PAD;

    let top, maxHeight;
    if (spaceBelow >= 220) {
      top       = rect.bottom + scrollY + 6;
      maxHeight = Math.min(DROPDOWN_H, spaceBelow);
    } else if (spaceAbove > spaceBelow) {
      maxHeight = Math.min(DROPDOWN_H, spaceAbove);
      top       = rect.top + scrollY - maxHeight - 6;
    } else {
      top       = rect.bottom + scrollY + 6;
      maxHeight = Math.max(160, spaceBelow);
    }

    setPortalStyle({
      position: 'absolute',
      top,
      left,
      width: DROPDOWN_W,
      maxHeight,
      zIndex: 99999,
    });
    loadAssignData();
    setOpen(true);
  }, [open, loadAssignData]);

  const handleAssign = useCallback(async (agent, group) => {
    setSaving(true);
    try {
      const payload = { assigned_to: agent.id };
      if (group?.id) payload.assigned_group = group.id;
      await ticketsAPI.update(ticketId, payload);
      onAssigned && onAssigned(agent, group);
    } catch (e) {
      console.error('Assign failed', e);
    } finally {
      setSaving(false);
      setOpen(false);
      setSearch('');
    }
  }, [ticketId, onAssigned]);

  const handleUnassign = useCallback(async () => {
    setSaving(true);
    try {
      await ticketsAPI.update(ticketId, { assigned_to: null, assigned_group: null });
      onAssigned && onAssigned(null, null);
    } catch (e) {
      console.error('Unassign failed', e);
    } finally {
      setSaving(false);
      setOpen(false);
    }
  }, [ticketId, onAssigned]);

  const searchLower    = search.toLowerCase();
  const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(searchLower));
  const rawMembers     = activeGroup?.members || [];
  const displayMembers = search ? rawMembers.filter(m => m.name.toLowerCase().includes(searchLower)) : rawMembers;
  const filteredAgents = agents.filter(a => a.name.toLowerCase().includes(searchLower));

  const panel = open && ReactDOM.createPortal(
    <div
      ref={dropRef}
      style={{
        ...portalStyle,
        background: '#FFFFFF',
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}`, background: '#F8FAFC', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFFFFF', border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 11px' }}>
          <Icon name="search" size={14} color={T.muted} />
          <input
            autoFocus
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by group or agent..."
            style={{ border: 'none', outline: 'none', flex: 1, fontSize: 13, fontFamily: 'inherit', color: '#1A202C', background: 'transparent' }}
          />
          {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: T.muted, padding: 0 }}>✕</button>}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          {['groups', 'agents'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700,
              background: tab === t ? T.active : T.g50,
              color: tab === t ? '#FFFFFF' : T.muted,
              textTransform: 'capitalize', transition: 'all 0.15s',
            }}>
              {t} <span style={{ opacity: 0.8 }}>{t === 'groups' ? groups.length : agents.length}</span>
            </button>
          ))}
        </div>
      </div>

      {/* None option */}
      <button onClick={handleUnassign} style={{
        width: '100%', textAlign: 'left', padding: '10px 14px',
        border: 'none', borderBottom: `1px solid ${T.border}`,
        background: '#FAFAFA', color: T.muted, fontSize: 12,
        fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <div style={{ width: 24, height: 24, borderRadius: '50%', background: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name="close" size={10} color={T.muted} />
        </div>
        None (Unassign)
      </button>

      {/* Body */}
      {tab === 'groups' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* Groups list */}
          <div style={{ borderRight: `1px solid ${T.border}`, overflowY: 'auto', background: '#FAFAFA' }}>
            <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Groups</div>
            {filteredGroups.length === 0 && <div style={{ padding: '12px', fontSize: 12, color: T.muted }}>No groups found</div>}
            {filteredGroups.map(group => {
              const isActive = activeGroup?.id === group.id;
              return (
                <button key={group.id} onClick={() => setActiveGroup(group)} style={{
                  width: '100%', textAlign: 'left', padding: '9px 12px',
                  border: 'none', borderLeft: isActive ? `3px solid ${T.active}` : '3px solid transparent',
                  background: isActive ? '#FFFFFF' : 'transparent',
                  color: isActive ? '#1A202C' : '#4A5568',
                  fontSize: 12, fontWeight: isActive ? 700 : 500, cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  transition: 'all 0.1s',
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</span>
                  <Icon name="chevronR" size={10} color={isActive ? T.active : T.border} />
                </button>
              );
            })}
          </div>

          {/* Members of active group */}
          <div style={{ overflowY: 'auto', background: '#FFFFFF' }}>
            <div style={{ padding: '8px 12px', fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
              {activeGroup ? activeGroup.name : 'Agents'}
            </div>
                {loading && <div style={{ padding: '12px', fontSize: 12, color: T.muted }}>Loading…</div>}
                {!loading && displayMembers.length === 0 && <div style={{ padding: '12px', fontSize: 12, color: T.muted }}>No agents in group</div>}
                {displayMembers.map(member => {
                  const isCurrent = (value?.id && value.id === member.id) || (value?.name && value.name === member.name);
                  return (
                    <button key={member.id || member.name} onClick={() => handleAssign(member, activeGroup)} style={{
                  width: '100%', textAlign: 'left', padding: '9px 12px',
                  border: 'none', background: isCurrent ? T.g50 : 'transparent',
                  color: '#1A202C', fontSize: 12, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8, transition: 'background 0.1s',
                }}>
                  <Avatar name={member.name} size={24} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.name}</span>
                  {isCurrent && <span style={{ fontSize: 10, color: T.active, fontWeight: 700 }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {filteredAgents.length === 0 && <div style={{ padding: '20px', fontSize: 12, color: T.muted, textAlign: 'center' }}>No agents found</div>}
          {filteredAgents.map(agent => {
            const isCurrent = (value?.id && value.id === agent.id) || (value?.name && value.name === agent.name);
            return (
              <button key={agent.id} onClick={() => handleAssign(agent, null)} style={{
                width: '100%', textAlign: 'left', padding: '10px 14px',
                border: 'none', borderBottom: `1px solid ${T.border}`,
                background: isCurrent ? T.g50 : '#FFFFFF',
                color: '#1A202C', fontSize: 12, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10, transition: 'background 0.1s',
              }}>
                <Avatar name={agent.name} size={28} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#1A202C' }}>{agent.name}</div>
                  <div style={{ fontSize: 11, color: T.muted }}>{agent.email}</div>
                </div>
                {isCurrent && <span style={{ fontSize: 11, background: T.g50, color: T.active, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>Assigned</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={btnRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={handleOpen}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', borderRadius: 6,
          border: open ? `1.5px solid ${T.active}` : '1.5px solid transparent',
          background: open ? T.g50 : 'transparent',
          cursor: 'pointer', transition: 'all 0.15s',
          maxWidth: 200, overflow: 'hidden',
        }}
        title={value?.name ? `Assigned to ${value.name}` : groupName ? `Group: ${groupName}` : 'Click to assign'}
      >
        {saving ? (
          <span style={{ fontSize: 11, color: T.muted }}>Saving…</span>
        ) : value?.name ? (
          <>
            <Avatar name={value.name} size={22} />
            <span style={{ fontSize: 12, color: '#1A202C', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value.name}</span>
          </>
        ) : groupName ? (
          <>
            <span style={{ fontSize: 14, lineHeight: 1 }}>👥</span>
            <span style={{ fontSize: 12, color: '#374151', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{groupName}</span>
          </>
        ) : (
          <span style={{ fontSize: 12, color: T.muted, fontStyle: 'italic' }}>Assign…</span>
        )}
        <Icon name={open ? 'chevronU' : 'chevronD'} size={10} color={T.muted} />
      </button>
      {panel}
    </div>
  );
}
